import config from '@/config'
import { getRoomProtobufTypes } from '@/proto/roomProtobuf'

class GlobalWsService {
  constructor () {
    this.ws = null
    this.token = ''
    this.connected = false
    this.listeners = new Set()
    this.reconnectTimer = null
    this.shouldReconnect = false
    this.lastOnlineUsers = null
    this.lastMapPlayers = null
    this.lastWorldNpcs = []
    this.lastWorldChests = []
    this.lastPokerState = null
    this.lastPokerPrivate = null
    const types = getRoomProtobufTypes()
    this.WsEnvelope = types.WsEnvelope
    this.ChatMessage = types.ChatMessage
    this.AudioChunk = types.AudioChunk
  }

  subscribe (cb) {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }

  getOnlineUsers () {
    return this.lastOnlineUsers
  }

  getMapPlayers () {
    return this.lastMapPlayers
  }

  getWorldState () {
    return {
      npcs: this.lastWorldNpcs,
      chests: this.lastWorldChests
    }
  }

  _emit (event) {
    this.listeners.forEach(listener => listener(event))
  }

  connect (token) {
    if (!token) return
    this.token = token
    this.shouldReconnect = true
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return
    }
    const wsUrl = config.getWsUrl(token)
    this.ws = new WebSocket(wsUrl)

    this.ws.onopen = () => {
      this.connected = true
      this._emit({ type: 'status', connected: true })
    }

    this.ws.onmessage = async (event) => {
      try {
        const data = event.data instanceof Blob
          ? new Uint8Array(await event.data.arrayBuffer())
          : new Uint8Array(event.data)
        const envelope = this.WsEnvelope.decode(data)
        if (envelope.chat) {
          const msg = envelope.chat
          const msgType = parseInt(msg.type)
          if (msgType === 1) {
            this._emit({ type: 'system', content: msg.content || '' })
          }
          if (msgType === 6) {
            try {
              this.lastOnlineUsers = JSON.parse(msg.content || '[]')
              this._emit({ type: 'onlineUsers', users: this.lastOnlineUsers })
            } catch (_) {
              // ignore malformed online list
            }
          }
          if (msgType === 13) {
            try {
              this.lastPokerState = JSON.parse(msg.content || '{}')
              this._emit({ type: 'pokerState', state: this.lastPokerState })
            } catch (_) {}
          }
          if (msgType === 14) {
            try {
              const priv = JSON.parse(msg.content || '{}')
              this.lastPokerPrivate = priv
              this._emit({ type: 'pokerPrivate', private: priv, to: msg.to })
            } catch (_) {}
          }
          if (msgType === 17) {
            this._emit({
              type: 'pokerRoomText',
              user: msg.user,
              content: msg.content,
              timestamp: msg.timestamp
            })
          }
          if (msgType === 8) {
            try {
              const payload = JSON.parse(msg.content || '{}')
              const players = {}
              for (const p of (payload.players || [])) {
                if (p.user) {
                  players[p.user] = {
                    x: p.x,
                    y: p.y,
                    outfit: p.outfit || null
                  }
                }
              }
              this.lastMapPlayers = players
              this.lastWorldNpcs = Array.isArray(payload.npcs) ? payload.npcs : []
              this.lastWorldChests = Array.isArray(payload.chests) ? payload.chests : []
              this._emit({
                type: 'mapState',
                players,
                npcs: this.lastWorldNpcs,
                chests: this.lastWorldChests
              })
            } catch (_) {
              // ignore malformed map state
            }
          }
          this._emit({ type: 'chat', msg })
        }
        if (envelope.audio) {
          this._emit({ type: 'audio', audio: envelope.audio })
        }
      } catch (_) {
        // ignore malformed events
      }
    }

    this.ws.onclose = () => {
      this.connected = false
      this.ws = null
      this._emit({ type: 'status', connected: false })
      if (this.shouldReconnect) {
        this._scheduleReconnect()
      }
    }

    this.ws.onerror = () => {
      this.connected = false
      this._emit({ type: 'status', connected: false })
    }
  }

  send (bytes) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(bytes)
    }
  }

  requestOnlineUsers () {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.ChatMessage || !this.WsEnvelope) {
      return
    }
    const msg = this.ChatMessage.create({
      user: '',
      room_id: 0,
      content: '',
      timestamp: Date.now(),
      type: 3 // QUERY_COUNT
    })
    this.send(this.WsEnvelope.encode(this.WsEnvelope.create({ chat: msg })).finish())
  }

  _sendChat (type, content = '') {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.ChatMessage || !this.WsEnvelope) {
      return
    }
    const msg = this.ChatMessage.create({
      user: '',
      room_id: 0,
      content,
      timestamp: Date.now(),
      type
    })
    this.send(this.WsEnvelope.encode(this.WsEnvelope.create({ chat: msg })).finish())
  }

  pokerSpectate () {
    this._sendChat(15)
  }

  pokerLeave () {
    this._sendChat(16)
  }

  pokerSit (seat) {
    this._sendChat(9, JSON.stringify({ seat }))
  }

  pokerStand () {
    this._sendChat(10)
  }

  pokerReady () {
    this._sendChat(11)
  }

  pokerAction (action, amount = 0) {
    this._sendChat(12, JSON.stringify({ action, amount }))
  }

  pokerRoomText (content) {
    this._sendChat(17, content)
  }

  sendPokerAudio (bytes, mimeType = 'audio/webm;codecs=opus') {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.WsEnvelope || !this.AudioChunk) {
      return
    }
    const audio = this.AudioChunk.create({
      sender: '',
      to: '__poker__',
      data: bytes,
      timestamp: Date.now(),
      mime_type: mimeType
    })
    this.send(this.WsEnvelope.encode(this.WsEnvelope.create({ audio })).finish())
  }

  sendPlayerMove (x, y) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.ChatMessage || !this.WsEnvelope) {
      return
    }
    const msg = this.ChatMessage.create({
      user: '',
      room_id: 0,
      content: JSON.stringify({ x, y }),
      timestamp: Date.now(),
      type: 7 // PLAYER_MOVE
    })
    this.send(this.WsEnvelope.encode(this.WsEnvelope.create({ chat: msg })).finish())
  }

  _scheduleReconnect () {
    if (this.reconnectTimer) return
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect(this.token)
    }, 2000)
  }

  disconnect () {
    this.shouldReconnect = false
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.connected = false
    this.lastOnlineUsers = null
    this.lastMapPlayers = null
    this.lastWorldNpcs = []
    this.lastWorldChests = []
    this._emit({ type: 'status', connected: false })
  }
}

const globalWs = new GlobalWsService()
export default globalWs
